import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatDate } from "@/lib/api";
import { toast } from "sonner";
import {
  MessageSquare,
  Flame,
  Gauge,
  Vote,
  Gamepad2,
  Scissors,
  CalendarCheck,
  TrendingUp,
  DollarSign,
  ArrowLeftRight,
  Crown,
  Landmark,
} from "lucide-react";
import ConfidenceMeter from "@/components/ConfidenceMeter";
import TitansTrivia from "@/components/TitansTrivia";
import GuessTheTitan from "@/components/GuessTheTitan";
import { PollsBoard } from "@/pages/Polls";
import { track } from "@/lib/analytics";
import ProfileBar, { useFanProfile } from "@/components/fanzone/ProfileBar";
import TitanGrid from "@/components/fanzone/TitanGrid";
import BlurReveal from "@/components/fanzone/BlurReveal";
import JerseyChallenge from "@/components/fanzone/JerseyChallenge";
import TitansHistory from "@/components/fanzone/TitansHistory";
import Leaderboard from "@/components/fanzone/Leaderboard";
import AchievementsWall from "@/components/fanzone/AchievementsWall";
import Roster53 from "@/components/fanzone/Roster53";
import SeasonPredictor from "@/components/fanzone/SeasonPredictor";
import StockMarket from "@/components/fanzone/StockMarket";
import SalaryCapGM from "@/components/fanzone/SalaryCapGM";
import TradeMachine from "@/components/fanzone/TradeMachine";

const FORUM_BG = "https://images.unsplash.com/photo-1565483276060-e6730c0cc6a1?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80";

const THREADS = [
  { id: "gameday", title: "Gameday Hot Takes", desc: "Live reactions as the action unfolds." },
  { id: "trade-talk", title: "Trade & Draft Talk", desc: "Front office moves, mock drafts, what-ifs." },
  { id: "two-tone", title: "Two-Tone Blue Pride", desc: "Tailgates, jerseys, stories — Titans culture." },
];

const TABS = [
  { k: "games", label: "Daily Games", icon: Gamepad2 },
  { k: "roster53", label: "Cut to 53", icon: Scissors },
  { k: "predict", label: "Predict the Season", icon: CalendarCheck },
  { k: "stock", label: "Stock Market", icon: TrendingUp },
  { k: "capgm", label: "Cap GM", icon: DollarSign },
  { k: "trade", label: "Trade Machine", icon: ArrowLeftRight },
  { k: "leaderboard", label: "Leaderboard", icon: Crown },
  { k: "hot-takes", label: "Hot Takes", icon: Flame },
  { k: "polls", label: "Polls", icon: Vote },
  { k: "confidence", label: "Confidence", icon: Gauge },
  { k: "heritage", label: "Heritage", icon: Landmark },
];

const ALIASES = { trivia: "games", guess: "games", guessthetitan: "games", "hot-takes": "hot-takes" };
const GAME_TABS = new Set(["games", "roster53", "predict", "stock", "capgm", "trade", "leaderboard"]);

function Thread({ thread }) {
  const [comments, setComments] = useState([]);
  const [author, setAuthor] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/comments", { params: { thread_id: thread.id } });
      setComments(data || []);
    } catch (e) {
      console.error("Thread load failed:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [thread.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!author || !body) return;
    try {
      const { data } = await api.post("/comments", { thread_id: thread.id, author, body });
      track("comment_post", { thread: thread.id });
      setComments((c) => [data, ...c]);
      setBody("");
      toast.success("Posted!");
    } catch (err) {
      console.error("Thread post failed:", err);
      toast.error("Failed to post");
    }
  };

  return (
    <div data-testid={`thread-${thread.id}`} className="titans-card p-6">
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="font-cond uppercase tracking-[0.25em] text-[10px] text-titans-blue">Thread</div>
          <h3 className="font-display text-2xl uppercase mt-1">{thread.title}</h3>
          <p className="text-titans-silver text-sm font-body">{thread.desc}</p>
        </div>
        <div className="text-titans-silver text-xs font-cond uppercase tracking-wider flex items-center gap-1">
          <MessageSquare size={12} /> {comments.length}
        </div>
      </div>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-5 gap-2 mt-4">
        <input
          value={author}
          onChange={(e) => setAuthor(e.target.value)}
          placeholder="Handle"
          data-testid={`forum-${thread.id}-author`}
          className="md:col-span-1 bg-titans-void border border-[rgba(138,141,143,0.3)] focus:border-titans-blue focus:outline-none px-3 py-2 font-body text-sm"
        />
        <input
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Take it…"
          data-testid={`forum-${thread.id}-body`}
          className="md:col-span-3 bg-titans-void border border-[rgba(138,141,143,0.3)] focus:border-titans-blue focus:outline-none px-3 py-2 font-body text-sm"
        />
        <button
          type="submit"
          data-testid={`forum-${thread.id}-submit`}
          className="bg-titans-red hover:bg-titans-red-hover font-cond uppercase tracking-wider text-xs px-4 py-2"
        >
          Post
        </button>
      </form>
      <div className="mt-5 space-y-3 max-h-80 overflow-y-auto">
        {loading && <div className="text-titans-silver text-sm">Loading…</div>}
        {!loading && comments.length === 0 && <div className="text-titans-silver text-sm">No takes yet. Be the first.</div>}
        {comments.map((c) => (
          <div key={c.id} className="border-b border-[rgba(138,141,143,0.15)] pb-3 last:border-0">
            <div className="flex items-center gap-2">
              <div className="bg-titans-red w-7 h-7 flex items-center justify-center font-display text-xs">
                {(c.author || "?")[0].toUpperCase()}
              </div>
              <div className="font-cond uppercase tracking-wider text-sm">{c.author}</div>
              <div className="text-titans-muted text-[10px] font-cond uppercase tracking-wider ml-auto">
                {formatDate(c.created_at, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </div>
            </div>
            <div className="text-white/90 font-body text-sm mt-1.5 ml-9">{c.body}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Community() {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get("tab");
  const resolved = ALIASES[raw] || raw;
  const tab = TABS.some((t) => t.k === resolved) ? resolved : "games";
  const profile = useFanProfile();

  const setTab = (k) => {
    if (k === "games") {
      const sp = new URLSearchParams(searchParams);
      sp.delete("tab");
      setSearchParams(sp);
    } else {
      setSearchParams({ tab: k });
    }
  };

  return (
    <div data-testid="fan-zone-page">
      <div className="relative h-72 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${FORUM_BG})` }} />
        <div className="absolute inset-0 bg-gradient-to-t from-titans-void via-titans-void/80 to-titans-void/30" />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-8 h-full flex items-end pb-10">
          <div>
            <div className="font-cond uppercase tracking-[0.3em] text-xs text-titans-blue">Play · Compete · Talk Ball</div>
            <h1 className="font-display text-5xl md:text-7xl uppercase tracking-wide">Fan Zone</h1>
            <p className="text-titans-silver font-body text-sm mt-1">
              Daily games, GM challenges, streaks and bragging rights for Two-Tone Blue Nation.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-10">
        <div className="flex gap-2 mb-8 border-b border-[rgba(138,141,143,0.2)] overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`community-tab-${t.k}`}
              className={`font-cond uppercase tracking-wider text-sm px-4 py-3 inline-flex items-center gap-2 border-b-2 transition-colors -mb-px whitespace-nowrap ${
                tab === t.k ? "border-titans-red text-white" : "border-transparent text-titans-silver hover:text-white"
              }`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        {GAME_TABS.has(tab) && (
          <div className="mb-6">
            <ProfileBar profile={profile} />
          </div>
        )}

        {tab === "games" && (
          <div data-testid="daily-games-panel" className="grid lg:grid-cols-2 gap-6 items-start">
            <TitanGrid />
            <BlurReveal />
            <JerseyChallenge />
            <TitansHistory />
            <GuessTheTitan />
            <TitansTrivia />
          </div>
        )}

        {tab === "roster53" && (
          <div data-testid="roster53-panel">
            <Roster53 />
          </div>
        )}

        {tab === "predict" && (
          <div data-testid="predict-panel">
            <SeasonPredictor />
          </div>
        )}

        {tab === "stock" && (
          <div data-testid="stock-panel">
            <StockMarket />
          </div>
        )}

        {tab === "capgm" && (
          <div data-testid="capgm-panel">
            <SalaryCapGM />
          </div>
        )}

        {tab === "trade" && (
          <div data-testid="trade-panel">
            <TradeMachine />
          </div>
        )}

        {tab === "leaderboard" && (
          <div data-testid="leaderboard-panel" className="grid lg:grid-cols-2 gap-6 items-start">
            <Leaderboard />
            <AchievementsWall unlocked={profile?.achievements || []} />
          </div>
        )}

        {tab === "hot-takes" && (
          <div data-testid="hot-takes-panel" className="grid md:grid-cols-2 gap-6">
            {THREADS.map((t) => (<Thread key={t.id} thread={t} />))}
          </div>
        )}

        {tab === "polls" && (
          <div data-testid="polls-panel">
            <PollsBoard />
          </div>
        )}

        {tab === "confidence" && (
          <div data-testid="confidence-panel">
            <ConfidenceMeter />
          </div>
        )}

        {tab === "heritage" && (
          <div data-testid="heritage-panel" className="titans-card p-6 space-y-4">
            <div className="flex items-center gap-2 text-titans-red">
              <Landmark size={18} />
              <div className="font-cond uppercase tracking-[0.28em] text-[11px]">Two-Tone Blue History</div>
            </div>
            <h3 className="font-display uppercase text-3xl md:text-4xl leading-tight">
              Titans Heritage
            </h3>
            <p className="text-titans-silver font-body text-sm md:text-base max-w-2xl">
              Franchise history, retired numbers, Hall of Famers, division titles, and the moments that made Two-Tone Blue.
              Dive into the full timeline, franchise records, and Ring of Honor.
            </p>
            <a
              href="/history"
              className="btn-hero inline-flex mt-2"
              data-testid="heritage-open-btn"
            >
              Open the Vault <Landmark size={14} />
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
